import { Criteria, GenericUserJobType, ReportType } from "@intentsify/types";

export const Endpoints = {
  Suggestions: {
    Topics: {
      Post: {
        List: "/suggestions/topics",
      },
    },
  },
  Signals: {
    Get: {
      All: `/signals`,
      Topics: `/signals/topics`,
      Keywords: `signals/keywords`,
      TopicsAggregated: `/signals/topic-aggregated`,
      PixelIdentifiers: `/signals/pixel-identifiers`,
    },
    Post: {
      Keywords: {
        Create: `signals/keywords`,
        Verify: `signals/keywords/verify`,
      },
      Topics: {
        Verify: `signals/topics/verify`,
      },
    },
  },
  GenericUserJobs: {
    Get: {
      Files: "/generic-user-jobs/files",
    },
  },
  TalTools: {
    BusinessDataAppend: {
      Post: `/tal-tools/${String(
        GenericUserJobType.BUSINESS_DATA_APPEND,
      )}/append`,
      TechnoProductFilters: `/tal-tools/${String(
        GenericUserJobType.BUSINESS_DATA_APPEND,
      )}/products`,
      Get: {
        BusinessEventsFilters: `/tal-tools/${String(
          GenericUserJobType.BUSINESS_DATA_APPEND,
        )}/filters`,
      },
    },
    CompanyNameToDomain: {
      Post: `/tal-tools/${String(
        GenericUserJobType.COMPANY_NAME_TO_DOMAIN,
      )}/request`,
      All: {
        Validate: (tempFileName: string) =>
          `/tal-tools/company-name-to-domain/validate/${tempFileName}`,
      },
    },
    SmarTAL: {
      Post: `/tal-tools/${String(GenericUserJobType.SMARTAL)}/request`,
    },
    Download: (jobId: number) => `/download/tal-tools/${jobId}`,
  },
  TargetPersonas: {
    Get: {
      All: "/target-persona/all",
      Paginated: "/target-persona",
    },
    Post: {
      Create: "/target-persona",
    },
    Put: {
      Batch: "/target-persona",
    },
    Delete: {
      Batch: "/target-persona",
    },
    Upload: "/target-persona/upload",
    BlocklistMetadata: {
      Get: "/target-persona/blocklist",
      Delete: "/target-persona/blocklist",
    },
    JobFunctions: {
      Get: {
        All: "/target-persona/job-functions",
      },
    },
    JobLevels: {
      Get: {
        All: "/target-persona/job-levels",
      },
    },
  },

  ContentLibrary: {
    All: {
      Upload: "/content-library/upload",
    },
    DraftAssets: {
      Get: {
        ListPaginated: "/content-library/draft-assets",
      },
      Post: {
        Review: (draftAssetId: number) =>
          `/content-library/draft-assets/${draftAssetId}/review`,
      },
      Delete: {
        Batch: "/content-library/draft-assets",
      },
    },
    ReviewedAssets: {
      Get: {
        ListPaginated: "/content-library/reviewed-assets",
      },
      Put: {
        Batch: "/content-library/reviewed-assets",
      },
      Delete: {
        Batch: "/content-library/reviewed-assets",
      },
    },
    Download: (fileName: string, id: string) =>
      `/download/content-library/${encodeURIComponent(
        fileName,
      )}/${encodeURIComponent(id)}`,
  },
  Users: {
    Post: {
      Create: "/users",
      ResetPassword: (email: string) =>
        `/users/reset-password/${email.toLowerCase()}`,
      Settings: {
        Notifications: (id: number) => `/users/${id}/settings/notifications`,
      },
    },
    Get: {
      List: "/users",
      ById: (id: number | string) => `/users/${id}`,
      Settings: {
        Notifications: (id: number) => `/users/${id}/settings/notifications`,
      },
    },
    Put: {
      Edit: (id: number | string) => `/users/${id}`,
      Agreement: (id: number | string) => `/users/${id}/agreement`,
      SetPassword: (id: number | string) => `/users/${id}/set-password/`,
    },
    Delete: (id: string | number) => `/users/${id}`,
  },
  DisplayCampaignMonitoring: {
    Get: "/display-campaign-monitoring",
    Refresh: "/display-campaign-monitoring/refresh",
  },
  UserAuditLog: {
    Get: {
      List: "/user-audit-log",
      ActivityTypes: "/user-audit-log/activity-types",
      Companies: "/user-audit-log/companies",
      Users: "/user-audit-log/users",
      Export: "/user-audit-log/export",
    },
  },
  Campaigns: {
    Get: {
      Autodiscovery: {
        DownloadMetadata: (id: number | string) =>
          `/campaigns/${id}/autodiscovery/download-metadata`,
      },
      Paginated: "/campaigns",
      Creators: "/campaigns/creators",
      UserCampaignsSummary: "/campaigns/summary",
      PaginatedFirmographicCompanies: `/firmographics/companies`,
      PaginatedFirmographicDomains: `/firmographics/domains`,
      ById: (id: number | string) => `/campaigns/${id}`,
      Meta: (id: number | string) => `/campaigns/${id}/meta`,
      DownloadMultipleCampaignData: () => `/campaigns/campaigns-files`,
      DownloadMultipleCampaignMetadata: () => `/campaigns/campaigns-metas`,
      DownloadMultipleCampaignAccountMappings: () =>
        `/campaigns/campaigns-accounts-mappings`,
      DownloadMultipleLeadActivationSummaries: () =>
        `/campaigns/lead-activation-summaries`,
      Brief: (id: number | string) => `/campaigns/${id}/brief`,
      Details: {
        Paginated: (id: number | string) => `/campaigns/${id}/details`,
      },
      BuyingGroup: (id: number) => `/campaigns/${id}/buying-group`,
      BuyingGroupInsights: (id: number) =>
        `/campaigns/${id}/buying-group/insights`,
      BuyingGroupTopPersonasWithIntent: (id: number) =>
        `/campaigns/${id}/buying-group/top-personas-with-intent`,
      QuantumDemandAvailability: (id: number) =>
        `/campaigns/${id}/quantum-demand/availability`,
      DownloadQuantumDemandReports: (id: number) =>
        `/campaigns/${id}/quantum-demand/reports`,
      DownloadBuyingGroupIntentFeed: (id: number) =>
        `/campaigns/${id}/buying-group/download-buying-group-intent-feed`,
      ContactIntentDownload: (id: number) =>
        `/campaigns/${id}/contact-intent-download`,
      BuyingGroupPersonaDetails: (id: number, accountId: number) =>
        `/campaigns/${id}/buying-group/${accountId}/persona-details`,
      BuyingGroupAvailableSignals: (id: number, accountId: number) =>
        `/campaigns/${id}/buying-group/${accountId}/available-signals`,
      BuyingGroupTopics: (id: number, accountId: number) =>
        `/campaigns/${id}/buying-group/${accountId}/topics`,
      BuyingGroupKeywords: (id: number, accountId: number) =>
        `/campaigns/${id}/buying-group/${accountId}/keywords`,
      BuyingGroupDynamic1: (id: number, accountId: number) =>
        `/campaigns/${id}/buying-group/${accountId}/dynamic1`,
      BuyingGroupWebVisits: (id: number, accountId: number) =>
        `/campaigns/${id}/buying-group/${accountId}/web-visits`,
      BuyingGroupListAllPersonas: (id: number, accountId: number) =>
        `/campaigns/${id}/buying-group/${accountId}/list-all-personas`,
      Domains: {
        TopPerformingDomains: (id: number) =>
          `/campaigns/${id}/domains/top-performing`,
        TopTrendingDomains: (id: number) =>
          `/campaigns/${id}/domains/top-trending`,
        TopPerformingDomainsWithTimeseries: (id: number) =>
          `/campaigns/${id}/domains/top-performing-with-timeseries`,
        Details: (id: number) => `/campaigns/${id}/domains/details`,
        DownloadTempFile: (campaignID: number, fileName: string) =>
          `/campaigns/${campaignID}/domains/download-temp-file/${fileName}`,
        DownloadDomainsFile: (campaignID: number, fileName: string) =>
          `/campaigns/${campaignID}/domains/download-domains-file/${fileName}`,
        DownloadBlocklistedDomainsFile: (
          campaignID: number,
          fileName: string,
        ) =>
          `/campaigns/${campaignID}/domains/download-blocklisted-domains-file/${fileName}`,
        DownloadTAL: (campaignID: number) =>
          `/campaigns/${campaignID}/domains/download-tal-file`,
        DownloadTALSummary: (campaignID: number) =>
          `/campaigns/${campaignID}/domains/download-tal-summary-file`,
        DownloadFirmographicsFile: (campaignID: number) =>
          `/campaigns/${campaignID}/domains/download-firmographics-file`,
      },
      Signals: {
        MostActiveSignals: (id: number) =>
          `/campaigns/${id}/signals/most-active`,
        SignalActivityWithTimeseries: (id: number) =>
          `/campaigns/${id}/signals/activity-with-timeseries`,
        AvailableSignalTypes: (id: number) =>
          `/campaigns/${id}/signals/available-signal-types`,
        TopSignals: (id: number) => `/campaigns/${id}/signals/top`,
      },
      Leads: {
        Paginated: (id: number) => `/campaigns/${id}/leads`,
        PaginatedAccounts: (id: number) => `/campaigns/${id}/leads/accounts`,
        DownloadSelection: (id: number) => `/campaigns/${id}/leads/download`,
        Performance: (id: number) => `/campaigns/${id}/leads/performance`,
        PerformanceFilters: (id: number) =>
          `/campaigns/${id}/leads/performance-filters`,
      },
      DataSources: `/campaigns/data-sources`,
      CorporateEntities: `/campaigns/corporate-entities`,
      RepresentativeNames: `/campaigns/representative-names`,
      CampaignManagers: `/campaigns/campaign-managers`,
      FirmographicsFilters: () => `/firmographics/filters`,
      CompanyBusinessUnits: (id: number | string) =>
        `/campaigns/${id}/business-units`,
      WeeklySnapshot: {
        AccountsInBuyerResearchStageStatistics: (id: number) =>
          `/campaigns/${id}/weekly-snapshot/accounts-in-buyer-research-stage-statistics`,
        CoverageByEmployeeSize: (id: number) =>
          `/campaigns/${id}/weekly-snapshot/coverage-by-employee-size`,
        CoverageByRevenueSize: (id: number) =>
          `/campaigns/${id}/weekly-snapshot/coverage-by-revenue-size`,
        CoverageByTopIndustries: (id: number) =>
          `/campaigns/${id}/weekly-snapshot/coverage-by-top-industries`,
        BuyerResearchStageMovement: (id: number) =>
          `/campaigns/${id}/weekly-snapshot/get-buyer-research-stage-movement`,
        MostEngagedAccounts: (id: number) =>
          `/campaigns/${id}/weekly-snapshot/most-engaged-accounts`,
        MostResearchedTopics: (id: number) =>
          `/campaigns/${id}/weekly-snapshot/most-researched-topics`,
        ContentAlignmentAnalysis: (id: number) =>
          `/campaigns/${id}/weekly-snapshot/content-alignment-analysis`,
        PingContent: (id: number) =>
          `/campaigns/${id}/weekly-snapshot/ping-content`,
        LeadByAssetPerformance: (id: number) =>
          `/campaigns/${id}/weekly-snapshot/lead-by-asset-performance`,
        TrendingTopics: (id: number) =>
          `/campaigns/${id}/weekly-snapshot/trending-topics`,
        TopCreativePerformance: (id: number) =>
          `/campaigns/${id}/weekly-snapshot/top-creative-performance`,
        TopicsByLocation: (id: string | number) =>
          `/campaigns/${id}/weekly-snapshot/topics-by-location`,
        ImpressionsByLocation: (id: string | number) =>
          `/campaigns/${id}/weekly-snapshot/impressions-by-location`,
        LeadsPerformance: (id: string | number) =>
          `/campaigns/${id}/weekly-snapshot/leads-performance`,
        TopAccountsBySpend: (id: string | number) =>
          `/campaigns/${id}/weekly-snapshot/top-accounts-by-spend`,
        AccountsReachedOverTime: (id: string | number) =>
          `/campaigns/${id}/weekly-snapshot/accounts-reached-over-time`,
        ImpressionsAndClicksOverTime: (id: string | number) =>
          `/campaigns/${id}/weekly-snapshot/impressions-and-clicks-over-time`,
      },
      Firmographics: {
        Download: `/firmographics-download`,
      },
      QuantumDemand: {
        DomainMeta: (campaignId: number) =>
          `/campaigns/${campaignId}/quantum-demand/domain-meta`,
        DomainContacts: (campaignId: number) =>
          `/campaigns/${campaignId}/quantum-demand/domain-contacts`,
        DomainTopics: (campaignId: number) =>
          `/campaigns/${campaignId}/quantum-demand/domain-topics`,
        DomainPersonas: (campaignId: number) =>
          `/campaigns/${campaignId}/quantum-demand/domain-personas`,
        DomainActivation: (campaignId: number) =>
          `/campaigns/${campaignId}/quantum-demand/domain-activation`,
      },
    },
    Post: {
      Autodiscovery: {
        Process: (id: number | string) => `/campaigns/${id}/autodiscovery`,
        MutateKeywordsAndTopics: (id: number | string) =>
          `/campaigns/${id}/autodiscovery/keywords-and-topics`,
        UploadFiles: (id: number) => `/campaigns/${id}/autodiscovery/files`,
      },
      Create: "/campaigns",
      Clone: (id: number) => `/campaigns/${id}/clone`,
      Domains: {
        SaveDomains: (id: number) => `/campaigns/${id}/domains/save`,
      },
      Signals: {
        KeywordsUpload: (id: number) =>
          `/campaigns/${id}/signals/keywords/upload`,
        TopicsUpload: (id: number) => `/campaigns/${id}/signals/topics/upload`,
        KeywordsSave: (id: number) => `/campaigns/${id}/signals/keywords/save`,
      },
      Firmographics: {
        Paginated: `/firmographics:filter`,
        Count: `/firmographics-count`,
      },
      QuantumDemand: {
        Paginated: (id: number) => `/campaigns/${id}/quantum-demand`,
      },
    },
    Put: {
      Settings: (id: number) => `/campaigns/${id}/settings`,
      AdvancedSettings: (id: number) => `/campaigns/${id}/advanced-settings`,
      BuyingGroup: (id: number) => `/campaigns/${id}/buying-group`,
      KeywordsAndTopics: (id: number) => `/campaigns/${id}/keywords-and-topics`,
      SaveAccounts: (id: number) => `/campaigns/${id}/accounts/save`,
      Autodiscovery: {
        DisableDynamic1: (id: number | string) =>
          `/campaigns/${id}/autodiscovery/disable-dynamic-one`,
        MutateKeywords: (id: number | string) =>
          `/campaigns/${id}/autodiscovery/keywords`,
        ExcludeDynamic1Article: (id: number | string) =>
          `/campaigns/${id}/autodiscovery/exclude-dynamic-one-article`,
      },
      UpdateCampaignFavoriteStatus: (id: number) =>
        `/campaigns/${id}/favorite-status`,
    },
    Delete: {
      Autodiscovery: {
        Url: (campaignId: number, urlId: string) =>
          `/campaigns/${campaignId}/autodiscovery/files/${urlId}`,
      },
    },
  },
  CampaignDetails: {
    Get: {
      List: (campaignId: number | string) => `/campaign-details/${campaignId}`,
      Filters: (campaignId: number | string) =>
        `/campaign-details/${campaignId}/filters`,
      AvailableSignals: (campaignId: number | string, domainId: number) =>
        `/campaign-details/${campaignId}/domains/${domainId}/available-signals`,
      AggregatedSignal: (
        campaignId: number | string,
        domainId: number,
        signalId: number,
      ) =>
        `/campaign-details/${campaignId}/domains/${domainId}/signals/${signalId}`,
      TopLocations: (campaignId: number | string, domainId: number) =>
        `/campaign-details/${campaignId}/domains/${domainId}/top-locations`,
      DownloadSelection: (id: number | string) =>
        `/campaign-details/${id}/download-selection`,
      DownloadBuyingGroup: (id: number | string) =>
        `/campaign-details/${id}/download-buying-group`,
      Summary: (id: number) => `/campaign-details/${id}/summary`,
      Spotlight: (campaignId: number | string, domainId: number) =>
        `/campaign-details/${campaignId}/domains/${domainId}/spotlight`,
    },
    POST: {
      DownloadLeadActivationSummaries: (campaignId: number) =>
        `/campaign-details/${campaignId}/domains/lead-activation-summary/download`,
    },
  },
  Representatives: {
    Get: {
      All: "/representatives",
    },
  },
  Roles: {
    Get: {
      All: "/roles",
    },
  },
  DigitalForecasting: {
    Post: {
      Forecast: () => `/digital-forecasting:forecast`,
    },
  },
  EmailCampaigns: {
    Get: {
      List: "/email-campaigns",
    },
  },
  Companies: {
    Post: {
      CreateCompany: "/companies",
      CreateBusinessUnit: (companyId: number) =>
        `/companies/${companyId}/business-units`,
      EstablishWorkatoMapping: (companyId: number) =>
        `/companies/${companyId}/integrations/workato-establish-mapping`,
      EstablishWorkatoIntegration: (companyId: number) =>
        `/companies/${companyId}/integrations/workato-establish-integration`,
      CreateAudienceSegment: (companyId: number) =>
        `/companies/${companyId}/audience-segments`,
      CreateCompanyPixel: (companyId: number) =>
        `/companies/${companyId}/pixels`,
      CreateCompanyPixelDeliveryRecipient: (
        companyId: number,
        pixelId: number,
      ) => `/companies/${companyId}/pixels/${pixelId}/delivery-recipients`,
    },
    Get: {
      Paginated: `/companies`,
      SsoConfig: (companyId: number) => `/companies/${companyId}/sso`,
      McpAccess: (companyId: number) => `/companies/${companyId}/mcp-access`,
      McpUsage: (companyId: number) =>
        `/companies/${companyId}/mcp-credit-usage`,
      McpUsageHistory: (companyId: number) =>
        `/companies/${companyId}/mcp-credit-usage/history`,
      All: "/companies/all",
      DataPackage: (id: string) => `/companies/${id}/data-package`,
      TrialEndDate: (id: string) => `/companies/${id}/trial-end-date`,
      ById: (id: string) => `/companies/${id}`,
      ListBusinessUnits: (companyId: number) =>
        `/companies/${companyId}/business-units`,
      ListActiveCampaigns: (companyId: number) =>
        `/companies/${companyId}/active-campaigns`,
      ListPastCampaigns: (companyId: number) =>
        `/companies/${companyId}/past-campaigns`,
      getWorkatoToken: (companyId: number, workatoCustomerId: number) =>
        `/companies/${companyId}/integrations/workato-embedded-token/${workatoCustomerId}`,
      listWorkatoConnections: (companyId: number, workatoCustomerId: number) =>
        `/companies/${companyId}/integrations/workato-connections/${workatoCustomerId}`,
      ListAudienceSegments: (companyId: number) =>
        `/companies/${companyId}/audience-segments/`,
      ListPixels: (companyId: number) => `/companies/${companyId}/pixels`,
      Pixel: (companyId: number, pixelId: number) =>
        `/companies/${companyId}/pixels/${pixelId}`,
      DownloadInstructions: `/companies/script-instructions`,
      PixelCampaigns: (companyId: number, pixelId: number) =>
        `/companies/${companyId}/pixels/${pixelId}/campaigns`,
      PixelDeliveryRecipients: (companyId: number, pixelId: number) =>
        `/companies/${companyId}/pixels/${pixelId}/delivery-recipients`,
      PixelDeliveryRecipient: (
        companyId: number,
        pixelId: number,
        deliveryRecipientId: number,
      ) =>
        `/companies/${companyId}/pixels/${pixelId}/delivery-recipients/${deliveryRecipientId}`,
    },
    Put: {
      EditCompany: (id: string | number) => `/companies/${id}`,
      SsoConfig: (companyId: number) => `/companies/${companyId}/sso`,
      McpAccess: (companyId: number) => `/companies/${companyId}/mcp-access`,
      McpCredits: (companyId: number) => `/companies/${companyId}/mcp-credits`,
      EditBusinessUnit: (companyId: number, unitId: number) =>
        `/companies/${companyId}/business-units/${unitId}`,
      AudienceSegment: (companyId: number, id: number) =>
        `/companies/${companyId}/audience-segments/${id}`,
      EditCompanyPixel: (companyId: number, pixelId: number) =>
        `/companies/${companyId}/pixels/${pixelId}`,
      EditCompanyPixelDeliveryRecipient: (
        companyId: number,
        pixelId: number,
        deliveryRecipientId: number,
      ) =>
        `/companies/${companyId}/pixels/${pixelId}/delivery-recipients/${deliveryRecipientId}`,
    },
    Delete: {
      ById: (id: string | number) => `/companies/${id}`,
      SsoConfig: (companyId: number) => `/companies/${companyId}/sso`,
      BusinessUnit: (companyId: number, unitId: number) =>
        `/companies/${companyId}/business-units/${unitId}`,
      AudienceSegment: (companyId: number, id: number) =>
        `/companies/${companyId}/audience-segments/${id}`,
      CompanyPixel: (companyId: number, pixelId: number) =>
        `/companies/${companyId}/pixels/${pixelId}`,
      PixelDeliveryRecipients: (
        companyId: number,
        pixelId: number,
        deliveryRecipientId: number,
      ) =>
        `/companies/${companyId}/pixels/${pixelId}/delivery-recipients/${deliveryRecipientId}`,
    },
  },
  BusinessEntities: {
    Post: {
      Create: "/business-entities",
    },
    Get: {
      Paginated: "/business-entities",
      ById: (id: number) => `/business-entities/${id}`,
      All: `/business-entities/all`,
    },
    Put: {
      Update: (id: number) => `/business-entities/${id}`,
    },
    Delete: {
      ById: (id: number) => `/business-entities/${id}`,
    },
  },
  Auth: {
    Post: {
      LogOut: "/auth/log-out",
      LogIn: "/auth/log-in",
    },
    Get: {
      Refresh: "/auth/refresh",
      Me: "/auth/me",
      SsoLookup: (email: string) =>
        `/auth/sso/lookup?email=${encodeURIComponent(email)}`,
    },
  },
  OAuth: {
    Interactions: {
      Get: (interactionId: string) =>
        `/oauth/interactions/${encodeURIComponent(interactionId)}`,
      Decision: (interactionId: string) =>
        `/oauth/interactions/${encodeURIComponent(interactionId)}/decision`,
    },
    ConnectedApps: {
      Get: "/oauth/connected-apps",
      Usage: "/oauth/connected-apps/mcp-credit-usage",
      UsageHistory: "/oauth/connected-apps/mcp-credit-usage/history",
      Delete: (grantId: string) =>
        `/oauth/connected-apps/${encodeURIComponent(grantId)}`,
    },
  },
  ProgrammaticCampaigns: {
    Get: {
      Paginated: "programmatic-campaigns",
      ById: (id: string) => `programmatic-campaigns/${id}`,
      Campaigns: (id: string) => `programmatic-campaigns/${id}/campaigns`,
    },
    ImpressionsAndClicks: {
      Get: {
        Statistics: (id: string) =>
          `programmatic-campaigns/${id}/digital-performance:getStatistics`,
        EngagementBreakdown: (id: string) =>
          `programmatic-campaigns/${id}/digital-performance:getEngagementBreakdown`,
        ImpressionsByLocation: (id: string | number) =>
          `programmatic-campaigns/${id}/digital-performance:getImpressionsByLocation`,
        TopCreativePerformance: (id: string) =>
          `programmatic-campaigns/${id}/digital-performance:topCreativePerformance`,
        TopicsByLocation: (id: string | number) =>
          `programmatic-campaigns/${id}/digital-performance:getTopicsByLocation`,
        SiteEngagementLift: (id: string) =>
          `programmatic-campaigns/${id}/digital-performance:getSiteEngagementLift`,
        IntegratedCampaignReport: (id: string) =>
          `programmatic-campaigns/${id}/digital-performance:getIntegratedCampaignReport`,
        PerformanceByDevice: (id: string) =>
          `programmatic-campaigns/${id}/digital-performance/performance-by-device`,
        MobileDeliveryBreakdown: (id: string) =>
          `programmatic-campaigns/${id}/digital-performance/mobile-delivery-breakdown`,
      },
    },
    Accounts: {
      Get: {
        AccountLifts: (id: string) =>
          `programmatic-campaigns/${id}/digital-performance:getAccountLifts`,
        TopAccounts: (id: string | number) =>
          `programmatic-campaigns/${id}/digital-performance:getTopAccounts`,
        AccountsInBuyerResearchStage: (id: string) =>
          `programmatic-campaigns/${id}/digital-performance:getAccountsInBuyerResearchStage`,
        AccountEngagement: (id: string) =>
          `programmatic-campaigns/${id}/digital-performance:getActiveAccountEngagements`,
        OverallViewability: (id: string) =>
          `programmatic-campaigns/${id}/digital-performance:getOverallViewability`,
        EngagementAcrossPersonas: (id: string) =>
          `programmatic-campaigns/${id}/digital-performance/engagement-across-personas`,
        AccountAndPersonasInsights: (id: string) =>
          `programmatic-campaigns/${id}/digital-performance/account-and-persona-insights`,
      },
    },
    CreativeEngagement: {
      Get: {
        CreativeWithHighestEngagement: (id: string) =>
          `programmatic-campaigns/${id}/digital-performance:getCreativesEngagements`,
        FatigueMap: (id: string) =>
          `programmatic-campaigns/${id}/digital-performance:getFatigueMap`,
        EngagementTestingResults: (id: string) =>
          `programmatic-campaigns/${id}/digital-performance:getCreativeEngagementTesting`,
        OverallVideoMap: (id: string) =>
          `programmatic-campaigns/${id}/digital-performance:getOverallVideoViews`,
        VideoCompletionRate: (id: string) =>
          `programmatic-campaigns/${id}/digital-performance:getVideoCompletionRate`,
        BuyerResearchStageEngagement: (id: string) =>
          `programmatic-campaigns/${id}/digital-performance:getBuyerResearchStageEngagement`,
      },
    },
    InnovidPerformance: {
      Get: {
        Statistics: (id: string) =>
          `programmatic-campaigns/${id}/innovid-performance:getStatistics`,
        EngagementBreakdown: (id: string) =>
          `programmatic-campaigns/${id}/innovid-performance:getEngagementBreakdown`,
        ImpressionsByLocation: (id: string | number) =>
          `programmatic-campaigns/${id}/innovid-performance:getImpressionsByLocation`,
        TopCreativePerformance: (id: string) =>
          `programmatic-campaigns/${id}/innovid-performance:topCreativePerformance`,
        TopicsByLocation: (id: string | number) =>
          `programmatic-campaigns/${id}/innovid-performance:getTopicsByLocation`,
        SiteEngagementLift: (id: string) =>
          `programmatic-campaigns/${id}/innovid-performance:getSiteEngagementLift`,
        IntegratedCampaignReport: (id: string) =>
          `programmatic-campaigns/${id}/innovid-performance:getIntegratedCampaignReport`,
        PerformanceByDevice: (id: string) =>
          `programmatic-campaigns/${id}/innovid-performance:getPerformanceByDevice`,
        MobileDeliveryBreakdown: (id: string) =>
          `programmatic-campaigns/${id}/innovid-performance:getMobileDeliveryBreakdown`,
        AccountLifts: (id: string) =>
          `programmatic-campaigns/${id}/innovid-performance:getAccountLifts`,
        TopAccounts: (id: string | number) =>
          `programmatic-campaigns/${id}/innovid-performance:getTopAccounts`,
        AccountsInBuyerResearchStage: (id: string) =>
          `programmatic-campaigns/${id}/innovid-performance:getAccountsInBuyerResearchStage`,
        AccountEngagement: (id: string) =>
          `programmatic-campaigns/${id}/innovid-performance:getActiveAccountEngagements`,
        OverallViewability: (id: string) =>
          `programmatic-campaigns/${id}/innovid-performance:getOverallViewability`,
        EngagementAcrossPersonas: (id: string) =>
          `programmatic-campaigns/${id}/innovid-performance:getEngagementAcrossPersonas`,
        AccountAndPersonasInsights: (id: string) =>
          `programmatic-campaigns/${id}/innovid-performance:getAccountAndPersonaInsights`,
        CreativeWithHighestEngagement: (id: string) =>
          `programmatic-campaigns/${id}/innovid-performance:getCreativesEngagements`,
        FatigueMap: (id: string) =>
          `programmatic-campaigns/${id}/innovid-performance:getFatigueMap`,
        EngagementTestingResults: (id: string) =>
          `programmatic-campaigns/${id}/innovid-performance:getCreativeEngagementTesting`,
        OverallVideoMap: (id: string) =>
          `programmatic-campaigns/${id}/innovid-performance:getOverallVideoViews`,
        VideoCompletionRate: (id: string) =>
          `programmatic-campaigns/${id}/innovid-performance:getVideoCompletionRate`,
        BuyerResearchStageEngagement: (id: string) =>
          `programmatic-campaigns/${id}/innovid-performance:getBuyerResearchStageEngagement`,
      },
    },
  },
  AutomatedProgrammaticSegment: {
    Get: {
      AutomatedProgrammaticSegments: "/automated-programmatic-segments",
      AutomatedProgrammaticSegment: (id: number) =>
        `/automated-programmatic-segments/${id}`,
      DownloadAutomatedProgrammaticSegment: (id: number) =>
        `/automated-programmatic-segments/${id}/download`,
      DownloadAll: "/automated-programmatic-segments/download",
      ActiveAccounts: `/automated-programmatic-segments/active-accounts`,
      Creators: "/automated-programmatic-segments/creators",
      Stats: "/automated-programmatic-segments/stats",
    },
    Put: {
      AutomatedProgrammaticSegment: (id: number) =>
        `/automated-programmatic-segments/${id}`,
      StopOrStartAutomatedProgrammaticSegment: (
        id: number,
        action: "start" | "stop",
      ) => `/automated-programmatic-segments/${id}/${action}`,
    },
    Post: {
      AutomatedProgrammaticSegment: "/automated-programmatic-segments",
      Count: (id: number) => `/automated-programmatic-segments/${id}/count`,
      DownloadDomains: (id: number) =>
        `/automated-programmatic-segments/${id}/downloadDomains`,
      ResolveExcludedDomains:
        "/automated-programmatic-segments/resolve-excluded-domains",
    },
  },
  ProgrammaticDomains: {
    Post: {
      Upload: "/programmatic-domains/upload",
    },
    Put: {
      Edit: (id: number) => `/programmatic-domains/${id}`,
    },
    Get: {
      Paginated: "/programmatic-domains",
      FileById: (id: number) => `/programmatic-domains/${id}`,
      Download: (id: number) => `/programmatic-domains/${id}/download`,
      TradedeskAdvertisers: () => `/programmatic-domains/tradedesk-advertisers`,
      AccountsCount: (id: number) =>
        `/programmatic-domains/${id}/accounts-count`,
      History: (id: number) => `/programmatic-domains/${id}/history`,
      DownloadAll: "/programmatic-domains/download",
    },
  },
  Admin: {
    Post: {
      ClearCache: "/admin/cache/clear",
    },
  },
  Forecasts: {
    Get: {
      Paginated: "/forecasts",
      Export: "/forecasts/export",
    },
    Post: {
      Create: "/forecasts",
    },
  },
  Publishers: {
    Get: {
      List: `/publishers`,
      ListAllCampaigns: "/publishers/list-all-campaigns",
      ListCampaigns: (id: string) => `publishers/${id}/campaigns`,
    },
    Post: {
      AssignCampaign: (publisherId: string) =>
        `/publishers/${publisherId}/campaigns`,
    },
    Delete: {
      UnassignCampaign: (publisherId: string, campaignId: number) =>
        `/publishers/${publisherId}/campaigns/${campaignId}`,
    },
  },
  Impersonation: {
    Post: {
      Enable: "/impersonation/enable",
      Disable: "/impersonation/disable",
    },
  },
  Domains: {
    Post: {
      IpResolution: "/domains/ip-resolution",
      VerifyDomains: "/domains/verify",
    },
  },
  Notifications: {
    Get: {
      Paginated: "/notifications",
    },
    Post: {
      MarkAsRead: "/notifications/mark-as-read",
      MarkAllAsRead: "/notifications/mark-all-as-read",
    },
  },
  Permissions: {
    Get: {
      PermissionModules: "/permissions/permission-modules",
      DataPackages: "/permissions/data-packages",
    },
  },
  UserAgencyCompany: {
    Get: "/user-agency-company",
    Post: {
      Select: (companyId: number) => `/user-agency-company/${companyId}`,
    },
  },
  Reports: {
    WeeklySnapshot: {
      Post: {
        RequestReport: (id: number) => `/reports/weekly-snapshot/${id}`,
      },
    },
    Spotlight: {
      Get: {
        AvailableSpotlightDeckWeeks: `/reports/spotlight/available-spotlight-deck-weeks`,
        AvailableSpotlightDeckAccounts: `/reports/spotlight/available-spotlight-deck-accounts`,
      },
      Post: {
        RequestSpotlightDeck: `/reports/spotlight/request-spotlight-deck`,
        RequestDeck: `/reports/spotlight/request-deck`,
      },
    },
    ProgramReview: {
      Post: {
        RequestProgramReview: `/reports/program-review/request-program-review-deck`,
      },
    },
    CumulativeReport: {
      Post: {
        Request: (id: string) => `/reports/cumulative/${id}`,
      },
      Get: {
        AdSolutionsReportDateRange: (id: string) =>
          `/reports/cumulative/${id}/ad-solutions-date-range`,
      },
    },
    MoatReport: {
      Post: {
        Request: (id: number) => `/reports/moat/${id}`,
      },
    },
    DownloadReport: {
      Get: ({
        campaignId,
        correlationId,
        reportType,
      }: {
        campaignId: string;
        correlationId: string;
        reportType: ReportType;
      }) => `/download/report/${campaignId}/${correlationId}/${reportType}`,
    },
  },
  Version: {
    Get: {
      Version: "/version",
    },
  },
  BulkOperations: {
    Post: {
      BulkEditCampaigns: "/bulk-operations/edit-campaigns",
    },
  },
  AccountsUpload: {
    All: {
      Upload: "/accounts-upload",
      Validate: (tempFileName: string) =>
        `/accounts-upload/validate/${tempFileName}`,
    },
    Get: {
      GetTempFileValidation: (tempFileName: string) =>
        `/accounts-upload/temp-file-validation/${encodeURIComponent(
          tempFileName,
        )}`,
    },
  },
  ManualReporting: {
    Get: {
      List: "/manual-reporting",
      DspProviders: "/manual-reporting/dsp-providers",
      Companies: "/manual-reporting/companies",
      Creators: "/manual-reporting/creators",
      Download: (id: number) => `/manual-reporting/${id}/download`,
      Template: "/manual-reporting/template",
      AcceptedCountries: "/manual-reporting/accepted-countries",
      GetTempFileValidation: (tempFileName: string) =>
        `/manual-reporting/temp-file-validation/${encodeURIComponent(
          tempFileName,
        )}`,
      Validate: (tempFileName: string) =>
        `/manual-reporting/validate/${tempFileName}`,
    },
    Post: {
      Create: "/manual-reporting",
    },
    All: {
      Upload: "/manual-reporting/upload",
    },
  },
  PersonalInfo: {
    Create: "/personal-info",
    Verify: (token: string) => `personal-info/confirm/${token}/verify`,
  },
  ContactSearch: {
    Post: {
      Search: "/contact-search/search",
      Export: "/contact-search/export",
    },
    Get: {
      Filters: "/contact-search/filters",
      SearchDomains: (query: string) => `/contact-search/domain/${query}`,
    },
  },
  WebAnalytics: {
    Get: {
      Accounts: (pixelId: number) => `/web-analytics/${pixelId}/accounts`,
      Statistics: (pixelId: number) => `/web-analytics/${pixelId}/statistics`,
      WebsiteVisits: (pixelId: number) =>
        `/web-analytics/${pixelId}/website-visits`,
      SiteEngagementLift: (pixelId: number, campaignId: number) =>
        `/web-analytics/${pixelId}/site-engagement-lift/${campaignId}`,
      TopVisitedUrls: (pixelId: number) =>
        `/web-analytics/${pixelId}/top-visited-urls`,
      CustomersByGeography: (pixelId: number) =>
        `/web-analytics/${pixelId}/customers-by-geography`,
      CustomersByEmployeeSize: (pixelId: number) =>
        `/web-analytics/${pixelId}/customers-by-employee-size`,
      CustomersByRevenueSize: (pixelId: number) =>
        `/web-analytics/${pixelId}/customers-by-revenue-size`,
      ListVisitingAccounts: (pixelId: number) =>
        `/web-analytics/${pixelId}/visiting-accounts`,
      DownloadSelection: (pixelId: number) =>
        `/web-analytics/${pixelId}/download`,
      DownloadContactWebsiteResolutionSample: (pixelId: number) =>
        `/web-analytics/${pixelId}/contact-website-resolution-sample`,
      AccountsTopUrls: (pixelId: number) =>
        `/web-analytics/${pixelId}/accounts-top-urls`,
      VisitedPages: (pixelId: number) =>
        `/web-analytics/${pixelId}/visited-pages`,
      BuyingGroupStatistics: (pixelId: number) =>
        `/web-analytics/${pixelId}/buying-group-statistics`,
      BuyingGroupTopVisitedUrls: (pixelId: number) =>
        `/web-analytics/${pixelId}/buying-group-top-visited-urls`,
      PersonaTopVisitors: (pixelId: number) =>
        `/web-analytics/${pixelId}/persona-top-visitors`,
      AccountsMonitoredInIntent: (pixelId: number) =>
        `/web-analytics/${pixelId}/accounts-monitored-in-intent`,
      ListPersonas: (pixelId: number) => `/web-analytics/${pixelId}/personas`,
    },
  },
  Dictionary: {
    Countries: "/dicts/countries",
  },
  Audience: {
    List: "/audiences/",
    Create: "/audiences",
    Explorer: "/audiences/explorer",
    Counts: (qs: string) => `/audiences/counts?${qs}`,
    Update: (id: number) => `/audiences/${id}`,
    Get: (id: number) => `/audiences/${id}`,
    CriteriaCounts: (criteria: Criteria) => `/audiences/criteria/${criteria}`,
    Export: "/audiences/export",
    Breakdown: (
      id: number,
      type: string,
      metric: string,
      criterionLabel?: string,
    ) =>
      `/audiences/${id}/breakdown?type=${type}&metric=${metric}${
        criterionLabel
          ? `&criterionLabel=${encodeURIComponent(criterionLabel)}`
          : ""
      }`,
    CriteriaBreakdown: (
      type: string,
      metric: string,
      criteria: Criteria,
      intentAudienceId?: number,
    ) => {
      const url = `/audiences/criteria/${criteria}/breakdown?type=${type}&metric=${metric}`;

      if (intentAudienceId) {
        return `${url}&id=${intentAudienceId}`;
      }

      return url;
    },
  },
};
